import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PDFCoverPage, PDFFooter, PDFHeader } from './pdf-shared'
import { pdfColors, pdfStyles, severityColors, statusColors } from './pdf-theme'
import { fmtCurrency, fmtDate, fmtDateTime, fmtNumber, labelSeverity, labelStatus, labelType } from './pdf-utils'

export interface SavingsAuditData {
  tenant: { id: string; name: string; slug: string; plan: string } | null
  generatedAt: string
  period: { from: string; to: string }
  kpis: {
    appliedCount: number
    appliedTotalSaving: number
    dismissedCount: number
    dismissedTotalSaving: number
    pendingCount: number
    pendingTotalSaving: number
  }
  timeline: Array<{
    id: string
    type: string
    severity: string
    title: string
    status: string
    estimated_monthly_saving: number
    projected_saving: number
    applied_at: string | null
    dismissed_at: string | null
    notes: string | null
    license_display_name: string | null
    user_display_name: string | null
  }>
}

export function SavingsAuditPDF({ data }: { data: SavingsAuditData }) {
  const clientName = data.tenant?.name ?? 'Cliente'
  const k = data.kpis
  const periodLabel = formatPeriod(data.period.from, data.period.to)

  // Top items by saving (chart)
  const topItems = [...data.timeline]
    .filter((r) => r.status === 'applied')
    .sort((a, b) => b.projected_saving - a.projected_saving)
    .slice(0, 6)
  const maxItem = topItems[0]?.projected_saving ?? 1

  return (
    <Document
      title={`ROI365 - Auditoria de Ahorros - ${clientName}`}
      author="ROI365"
      subject="Auditoria de ahorros generados por las recomendaciones aplicadas"
    >
      <PDFCoverPage
        reportTitle="Auditoria de Ahorros"
        reportDescription="Justificacion del ahorro proyectado conseguido a partir de las recomendaciones aplicadas y descartadas en el periodo seleccionado."
        clientName={clientName}
        period={periodLabel}
        generatedAt={fmtDateTime(data.generatedAt)}
      />

      <Page size="A4" style={pdfStyles.page}>
        <PDFHeader title="Auditoria de Ahorros" subtitle={`${clientName} · ${periodLabel}`} />
        <PDFFooter />

        <Text style={pdfStyles.h2}>Resumen del periodo</Text>
        <View style={pdfStyles.kpiGrid}>
          <View style={[pdfStyles.kpiCard, { width: '32%', borderLeftColor: pdfColors.success }]}>
            <Text style={pdfStyles.kpiCardLabel}>Aplicadas</Text>
            <Text style={pdfStyles.kpiCardValue}>{fmtNumber(k.appliedCount)}</Text>
            <Text style={pdfStyles.kpiCardSub}>{fmtCurrency(k.appliedTotalSaving)} proyectado</Text>
          </View>
          <View style={[pdfStyles.kpiCard, { width: '32%', borderLeftColor: pdfColors.textTertiary }]}>
            <Text style={pdfStyles.kpiCardLabel}>Descartadas</Text>
            <Text style={pdfStyles.kpiCardValue}>{fmtNumber(k.dismissedCount)}</Text>
            <Text style={pdfStyles.kpiCardSub}>{fmtCurrency(k.dismissedTotalSaving)} proyectado</Text>
          </View>
          <View style={[pdfStyles.kpiCard, { width: '32%', borderLeftColor: pdfColors.warning }]}>
            <Text style={pdfStyles.kpiCardLabel}>Pendientes (actuales)</Text>
            <Text style={pdfStyles.kpiCardValue}>{fmtNumber(k.pendingCount)}</Text>
            <Text style={pdfStyles.kpiCardSub}>{fmtCurrency(k.pendingTotalSaving)} potencial</Text>
          </View>
        </View>

        {topItems.length > 0 ? (
          <>
            <Text style={pdfStyles.h2}>Mayores ahorros aplicados en el periodo</Text>
            <View style={{ marginTop: 4 }}>
              {topItems.map((r) => {
                const pct = (r.projected_saving / maxItem) * 100
                return (
                  <View key={r.id} style={pdfStyles.barChartItem}>
                    <View style={pdfStyles.barChartLabelRow}>
                      <Text style={[pdfStyles.barChartLabel, { maxWidth: '70%' }]}>
                        {r.title.length > 70 ? r.title.slice(0, 67) + '...' : r.title}
                      </Text>
                      <Text style={[pdfStyles.barChartValue, { color: pdfColors.success }]}>
                        {fmtCurrency(r.projected_saving)}
                      </Text>
                    </View>
                    <View style={pdfStyles.barTrack}>
                      <View style={[pdfStyles.barFill, { width: `${pct}%`, backgroundColor: pdfColors.success }]} />
                    </View>
                  </View>
                )
              })}
            </View>
          </>
        ) : null}

        <Text style={pdfStyles.h2}>Recomendaciones descartadas y motivo</Text>
        {(() => {
          const dismissedItems = data.timeline.filter((r) => r.status === 'dismissed')
          if (dismissedItems.length === 0) {
            return (
              <Text style={pdfStyles.paragraph}>
                No hay recomendaciones descartadas en el periodo seleccionado.
              </Text>
            )
          }
          return (
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableHeaderRow}>
                <Text style={[pdfStyles.tableHeaderCell, { width: '12%' }]}>Fecha</Text>
                <Text style={[pdfStyles.tableHeaderCell, { width: '32%' }]}>Recomendacion</Text>
                <Text style={[pdfStyles.tableHeaderCell, { width: '14%' }]}>Tipo</Text>
                <Text style={[pdfStyles.tableHeaderCell, { width: '12%', textAlign: 'right' }]}>Ahorro/mes</Text>
                <Text style={[pdfStyles.tableHeaderCell, { width: '30%' }]}>Motivo del descarte</Text>
              </View>
              {dismissedItems.map((r, idx) => (
                <View key={r.id} style={[pdfStyles.tableRow, idx % 2 === 1 ? pdfStyles.tableRowAlt : {}]} wrap={false}>
                  <Text style={[pdfStyles.tableCell, { width: '12%' }]}>{fmtDate(r.dismissed_at)}</Text>
                  <View style={[pdfStyles.tableCell, { width: '32%' }]}>
                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{r.title}</Text>
                    {r.license_display_name ? (
                      <Text style={{ fontSize: 7.5, color: pdfColors.textTertiary, marginTop: 1 }}>
                        {r.license_display_name}
                        {r.user_display_name ? ` · ${r.user_display_name}` : ''}
                      </Text>
                    ) : null}
                  </View>
                  <Text style={[pdfStyles.tableCell, { width: '14%' }]}>{labelType(r.type)}</Text>
                  <Text style={[pdfStyles.tableCell, { width: '12%', textAlign: 'right' }]}>
                    {fmtCurrency(r.estimated_monthly_saving)}
                  </Text>
                  <Text style={[pdfStyles.tableCell, { width: '30%' }]}>{extractDismissReason(r.notes)}</Text>
                </View>
              ))}
            </View>
          )
        })()}

        <Text style={pdfStyles.h2}>Cronologia de acciones</Text>
        {data.timeline.length === 0 ? (
          <Text style={pdfStyles.paragraph}>
            No se registraron recomendaciones aplicadas o descartadas dentro del periodo seleccionado.
          </Text>
        ) : (
          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeaderRow}>
              <Text style={[pdfStyles.tableHeaderCell, { width: '12%' }]}>Fecha</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '10%' }]}>Estado</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '32%' }]}>Recomendacion</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '14%' }]}>Tipo</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '10%' }]}>Severidad</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '11%', textAlign: 'right' }]}>Mensual</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '11%', textAlign: 'right' }]}>Proyectado</Text>
            </View>
            {data.timeline.map((r, idx) => {
              const date = r.applied_at ?? r.dismissed_at
              return (
                <View key={r.id} style={[pdfStyles.tableRow, idx % 2 === 1 ? pdfStyles.tableRowAlt : {}]} wrap={false}>
                  <Text style={[pdfStyles.tableCell, { width: '12%' }]}>{fmtDate(date)}</Text>
                  <Text
                    style={[
                      pdfStyles.tableCell,
                      { width: '10%', color: statusColors[r.status] ?? pdfColors.textPrimary, fontFamily: 'Helvetica-Bold' },
                    ]}
                  >
                    {labelStatus(r.status)}
                  </Text>
                  <View style={[pdfStyles.tableCell, { width: '32%' }]}>
                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{r.title}</Text>
                    {r.license_display_name ? (
                      <Text style={{ fontSize: 7.5, color: pdfColors.textTertiary, marginTop: 1 }}>
                        {r.license_display_name}
                        {r.user_display_name ? ` · ${r.user_display_name}` : ''}
                      </Text>
                    ) : null}
                  </View>
                  <Text style={[pdfStyles.tableCell, { width: '14%' }]}>{labelType(r.type)}</Text>
                  <Text
                    style={[
                      pdfStyles.tableCell,
                      { width: '10%', color: severityColors[r.severity] ?? pdfColors.textPrimary, fontFamily: 'Helvetica-Bold' },
                    ]}
                  >
                    {labelSeverity(r.severity)}
                  </Text>
                  <Text style={[pdfStyles.tableCell, { width: '11%', textAlign: 'right' }]}>
                    {fmtCurrency(r.estimated_monthly_saving)}
                  </Text>
                  <Text
                    style={[
                      pdfStyles.tableCell,
                      {
                        width: '11%',
                        textAlign: 'right',
                        fontFamily: 'Helvetica-Bold',
                        color: r.status === 'applied' ? pdfColors.success : pdfColors.textTertiary,
                      },
                    ]}
                  >
                    {r.status === 'applied' ? fmtCurrency(r.projected_saving) : '—'}
                  </Text>
                </View>
              )
            })}
          </View>
        )}

        <Text style={[pdfStyles.small, { marginTop: 12 }]}>
          El ahorro proyectado de cada recomendacion aplicada se calcula desde la fecha de
          aplicacion hasta el vencimiento de la licencia (o 12 meses por defecto si la licencia
          no tiene fecha de vencimiento informada).
        </Text>
      </Page>
    </Document>
  )
}

function extractDismissReason(notes: string | null): string {
  if (!notes) return '—'
  // El backend guarda el motivo como "Motivo descarte: <texto>", a veces precedido por
  // notas previas separadas por dos saltos de linea.
  const match = notes.match(/Motivo descarte:\s*([\s\S]+)$/)
  const raw = (match?.[1] ?? notes).trim()
  return raw.length > 0 ? raw : '—'
}

function formatPeriod(fromRaw: string, toRaw: string): string {
  // The backend sends ISO-like strings or SQL datetime expressions for defaults.
  // Try to extract dates and format. Fall back to raw if parsing fails.
  const tryParse = (s: string) => {
    const m = s.match(/(\d{4}-\d{2}-\d{2})/)
    return m ? m[1] : s
  }
  return `${tryParse(fromRaw)} → ${tryParse(toRaw)}`
}
